import { getTruncatedLabel } from '@/components/page-specific/catering/supplier-menu/components/sidebar-filter/CatergoryFilter'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoIcon from '@mui/icons-material/Info'
import {
  Box,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Stack,
  Chip,
  Avatar,
  Tooltip,
  useTheme,
} from '@mui/material'

import { useEffect, useMemo, useState } from 'react'

const FILTER_TOOLTIPS: Record<string, string> = {
  vegan: 'Plant-based',
  vegetarian: 'Meat free meals',
  powerplant: '2+ servings of Fruit or Veg',
  lowcarb: 'Under 25% carbs',
  highprotein: 'More than 20% protein',
  isPromoted: 'Boost your gut microbes',
}

type FilterAccordionProps = {
  label: string
  category: string
  filterOptions: {
    selected: boolean
    quantity: number
    displayName: string
    value: string
    logoUrl?: string | null
  }[]
  onFilterChange: (filterCategory: string, filter: string) => void
}

export const FilterAccordion: React.FC<FilterAccordionProps> = ({
  label,
  category,
  filterOptions,
  onFilterChange = () => {},
}) => {
  const maxNameLength = useMemo(() => {
    if (category === 'vendors') return 15
    // budget option labels ("Within Subsidy + Credits") are longer phrases,
    // not single dietary/allergen words, so they need a longer cutoff
    if (category === 'budget') return 40
    return 17
  }, [category])

  const selectedFilterCount = useMemo(
    () => filterOptions.filter((o) => o.selected).length,
    [filterOptions],
  )

  const theme = useTheme()

  // options can arrive after mount (vendors load async) — auto-expand when they land
  const hasOptions = filterOptions.length > 0
  const [expanded, setExpanded] = useState(hasOptions)
  useEffect(() => {
    if (hasOptions) setExpanded(true)
  }, [hasOptions])

  const handleClick = (category: string, value: string) => {
    onFilterChange(category, value)
  }

  return (
    <Box
      sx={(t) => ({
        flex: 1,
        borderRadius: 4,
        border: `1px solid ${t.palette.background.border}`,
        bgcolor: 'background.subtle',
        overflow: 'auto',
        scrollbarWidth: 'none',
      })}>
      <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'background.subtle' }}>
          <Typography>{`${label}${selectedFilterCount > 0 ? ` (${selectedFilterCount})` : ''}`}</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={(t) => ({
            padding: 1,
            bgcolor: 'background.subtle',
            borderTop: `1px solid ${t.palette.background.border}`,
          })}>
          <Stack spacing={1}>
            {filterOptions.map(({ displayName, value, selected, quantity, logoUrl }) => {
              const tooltip = FILTER_TOOLTIPS[value]
              const isTruncated = displayName.length > maxNameLength
              return (
                <Chip
                  key={`${displayName}-filter-option`}
                  disabled={quantity === 0}
                  avatar={logoUrl ? <Avatar src={logoUrl} /> : undefined}
                  onClick={() => handleClick(category, value)}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Tooltip title={isTruncated ? displayName : ''} placement="top" arrow>
                        <span>{`${getTruncatedLabel(displayName, maxNameLength)} (${quantity})`}</span>
                      </Tooltip>
                      {tooltip && (
                        <Tooltip title={tooltip} placement="top" arrow>
                          <InfoIcon
                            sx={{
                              fontSize: 14,
                              color: selected ? 'rgba(255,255,255,0.9)' : 'text.secondary',
                            }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  }
                  sx={{
                    fontSize: 12,
                    borderRadius: `${theme.shape.borderRadiusChip}px`,
                    backgroundColor: selected ? theme.palette.primary.main : 'transparent',
                    color: selected
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                    '& .MuiChip-label': {
                      width: '100%',
                    },
                    '&:hover': {
                      backgroundColor: selected
                        ? theme.palette.primary.dark
                        : theme.palette.background.hover,
                    },
                  }}
                />
              )
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
